import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Modal, ModalBody } from 'reactstrap';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import Select from 'react-select';
import { components as selectComponents } from 'react-select';
import debounce from 'lodash/debounce';
import CustomButton from '../OnboardingComponents/CustomButton';
import CustomInput from '../OnboardingComponents/CustomInput';
import PreferredCitySelect from '../Common/PreferredCitySelect';
import CollaboratorSearchSelect, {
  collabSelectStyles,
  getCollaboratorUserId,
  isUuid,
} from '../DashboardComponents/CollaboratorSearchSelect';
import { Avatar } from '../DashboardComponents/AvatarGroup';
import { ICONS } from '../../constants/icons';
import { get } from '../../api/axios';
import endpoints from '../../api/endpoints';
import { getUserAvatarUrl } from '../../helpers/userAvatar';

const SEARCH_DEBOUNCE_MS = 300;
const PROJECT_PAGE_SIZE = 10;
const INVITATION_PAGE_SIZE = 20;
export const WORKING_NOTE_MAX_LENGTH = 140;

const workingNoteSchema = Yup.string()
  .trim()
  .required('Working note is required')
  .max(
    WORKING_NOTE_MAX_LENGTH,
    `Working note must be ${WORKING_NOTE_MAX_LENGTH} characters or less`
  );

const soloSessionSchema = Yup.object({
  note: workingNoteSchema,
});

const shadowSessionSchema = Yup.object({
  note: workingNoteSchema,
  collaborators: Yup.array().min(1, 'Select at least one collaborator'),
});

const cityShadowSessionSchema = Yup.object({
  cityId: Yup.string().trim().required('Please select a city'),
  visibility: Yup.string()
    .oneOf(['global', 'private'])
    .required('Please select visibility'),
  collaborators: Yup.array().when('visibility', {
    is: 'private',
    then: schema =>
      schema
        .of(
          Yup.object({
            value: Yup.string()
              .test('uuid', 'Collaborator must have a valid user id', value =>
                isUuid(value)
              )
              .required(),
          }).unknown(true)
        )
        .min(1, 'Select at least one collaborator from this city')
        .required('Select at least one collaborator from this city'),
    otherwise: schema => schema.notRequired(),
  }),
  note: workingNoteSchema,
});

const SOLO_INITIAL_VALUES = { note: '' };
const SHADOW_INITIAL_VALUES = { note: '', collaborators: [] };
const CITY_SHADOW_INITIAL_VALUES = {
  cityId: '',
  visibility: 'global',
  collaborators: [],
  note: '',
};

const CITY_VISIBILITY_OPTIONS = [
  { value: 'global', label: 'Public' },
  { value: 'private', label: 'Private' },
];

const SESSION_STATUS_CLASS = {
  'In Session': 'in-session',
  Paused: 'paused',
  Invited: 'invited',
  Exited: 'exited',
};

const mapInvitationToCollabOption = (invitation, currentUserId) => {
  const user = invitation?.user || {};
  const userId = String(user.id || invitation?.user_id || '').trim();
  if (!userId || !isUuid(userId)) return null;
  if (currentUserId && String(userId) === String(currentUserId)) return null;

  const name =
    user.full_name ||
    [user.first_name, user.last_name].filter(Boolean).join(' ').trim() ||
    user.name ||
    user.email ||
    'Collaborator';
  const email = user.email || invitation?.email || '';

  return {
    value: userId,
    label: email ? `${name} (${email})` : name,
    name,
    email,
    avatar: getUserAvatarUrl(user),
    raw: Object.keys(user).length ? user : { id: userId },
  };
};

/**
 * Project shadow-session collaborator picker — active invitations only
 * via GET /api/v1/project-invitations (not project.members).
 */
const ProjectInvitationCollaboratorSelect = ({
  projectId,
  value = [],
  onChange,
  onBlur,
  disabled = false,
  error = null,
  enabled = true,
  inputId = 'shadow-session-collab-search',
}) => {
  const currentUserId = useSelector(state => state.auth?.currentUser?.id);
  const [inputValue, setInputValue] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [options, setOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const latestRequest = useRef(0);
  const abortRef = useRef(null);

  const debouncedSetQuery = useMemo(
    () => debounce(query => setSearchQuery(query), SEARCH_DEBOUNCE_MS),
    []
  );

  const loadInvitations = useCallback(
    async (query = '') => {
      if (!projectId) {
        setOptions([]);
        setIsLoading(false);
        return;
      }

      const requestId = ++latestRequest.current;
      if (abortRef.current) abortRef.current.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      setIsLoading(true);

      try {
        const response = await get(endpoints.getProjectInvitations, {
          signal: controller.signal,
          skipErrorToast: true,
          params: {
            involvement: 'all',
            status: 'active',
            project_id: projectId,
            page: 1,
            per_page: INVITATION_PAGE_SIZE,
            page_size: INVITATION_PAGE_SIZE,
            'with[]': ['user'],
            sort_by: 'created_at',
            sort_direction: 'desc',
            ...(query.trim() ? { search: query.trim() } : {}),
          },
        });

        if (requestId !== latestRequest.current) return;

        const result = response?.data?.result || {};
        const rows = Array.isArray(result) ? result : result?.data || [];
        const mapped = rows
          .map(row => mapInvitationToCollabOption(row, currentUserId))
          .filter(Boolean);

        const seen = new Set();
        setOptions(
          mapped.filter(option => {
            const key = String(option.value);
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
          })
        );
      } catch {
        if (requestId !== latestRequest.current) return;
        setOptions([]);
      } finally {
        if (requestId === latestRequest.current) setIsLoading(false);
      }
    },
    [projectId, currentUserId]
  );

  useEffect(() => {
    if (!enabled || !menuOpen || !projectId) return undefined;
    loadInvitations(searchQuery);
    return undefined;
  }, [enabled, menuOpen, projectId, searchQuery, loadInvitations]);

  useEffect(() => {
    setInputValue('');
    setSearchQuery('');
    setOptions([]);
    setMenuOpen(false);
  }, [projectId]);

  useEffect(
    () => () => {
      if (abortRef.current) abortRef.current.abort();
      debouncedSetQuery.cancel();
    },
    [debouncedSetQuery]
  );

  const mergedOptions = useMemo(() => {
    const selected = Array.isArray(value) ? value : [];
    if (!selected.length) return options;
    const seen = new Set(options.map(option => String(option.value)));
    return [
      ...options,
      ...selected.filter(option => !seen.has(String(option?.value || ''))),
    ];
  }, [options, value]);

  return (
    <div className="project-collab-modal project-collab-modal--invite project-session-modal__member-search">
      <div className="project-collab-modal__field">
        <label className="project-collab-modal__label" htmlFor={inputId}>
          Search Collaborator
        </label>
        <div
          className={`project-collab-modal__search${
            error ? ' project-collab-modal__search--error' : ''
          }`}
        >
          <img src={ICONS.messageSearchCollab} alt="" />
          <Select
            inputId={inputId}
            className="project-collab-modal__user-select"
            classNamePrefix="project-collab-modal-user"
            options={mergedOptions}
            value={value}
            onChange={selected => onChange?.(selected || [])}
            onBlur={onBlur}
            inputValue={inputValue}
            onInputChange={(next, meta) => {
              if (meta.action !== 'input-change') return;
              setInputValue(next);
              debouncedSetQuery(next);
            }}
            isMulti
            isSearchable
            isClearable={false}
            isDisabled={disabled || !projectId}
            isLoading={isLoading}
            closeMenuOnSelect={false}
            hideSelectedOptions={false}
            menuIsOpen={menuOpen}
            onMenuOpen={() => setMenuOpen(true)}
            onMenuClose={() => setMenuOpen(false)}
            placeholder={
              projectId
                ? 'Search by email or username...'
                : 'Select a project first'
            }
            noOptionsMessage={() =>
              !projectId
                ? 'Select a project first'
                : isLoading
                  ? 'Searching...'
                  : searchQuery
                    ? 'No collaborators found'
                    : 'No active collaborators'
            }
            loadingMessage={() => 'Searching...'}
            components={{
              Option: CollaboratorOption,
              MultiValueLabel: CollaboratorMultiValueLabel,
              IndicatorSeparator: null,
              DropdownIndicator: null,
            }}
            styles={collaboratorSelectStyles(Boolean(error))}
            menuPortalTarget={
              typeof document !== 'undefined' ? document.body : null
            }
            menuPosition="fixed"
            filterOption={() => true}
            aria-label="Search Collaborator"
          />
        </div>
        {error ? (
          <span className="project-collab-modal__error">{error}</span>
        ) : null}
      </div>
    </div>
  );
};

const CollaboratorOption = (props) => {
  const { data } = props;
  return (
    <selectComponents.Option {...props}>
      <span className="project-collab-modal__option">
        <Avatar
          src={data.avatar}
          name={data.name}
          size="xs"
          className="project-collab-modal__option-avatar"
        />
        <span className="project-collab-modal__option-meta">
          <span className="project-collab-modal__option-name">{data.name}</span>
          {data.email ? (
            <span className="project-collab-modal__option-email">{data.email}</span>
          ) : null}
        </span>
      </span>
    </selectComponents.Option>
  );
};

const CollaboratorMultiValueLabel = (props) => {
  const { data } = props;
  return (
    <selectComponents.MultiValueLabel {...props}>
      <span className="project-collab-modal__chip">
        <Avatar
          src={data.avatar}
          name={data.name}
          size="xs"
          className="project-collab-modal__chip-avatar"
        />
        <span className="project-collab-modal__chip-name">{data.name}</span>
      </span>
    </selectComponents.MultiValueLabel>
  );
};

const collaboratorSelectStyles = (hasError = false) => ({
  control: (base, state) => ({
    ...base,
    minHeight: 30,
    border: 'none',
    borderRadius: 12,
    backgroundColor: 'transparent',
    boxShadow: 'none',
    cursor: 'text',
    paddingLeft: 0,
    borderColor: hasError ? '#9b0000' : 'transparent',
    '&:hover': { border: 'none', backgroundColor: 'transparent' },
    ...(state.isFocused ? { outline: 'none' } : {}),
  }),
  valueContainer: (base) => ({
    ...base,
    padding: '0',
    gap: 6,
    minHeight: 30,
  }),
  container: (base) => ({
    ...base,
    width: '100%',
  }),
  input: (base) => ({
    ...base,
    margin: 0,
    padding: 0,
    color: '#0d0d0d',
    fontSize: 14,
    fontWeight: 500,
  }),
  placeholder: (base) => ({
    ...base,
    margin: 0,
    color: 'rgba(110, 110, 110, 0.7)',
    fontSize: 14,
    fontWeight: 500,
  }),
  multiValue: (base) => ({
    ...base,
    margin: 0,
    backgroundColor: '#ecf0fa',
    borderRadius: 999,
  }),
  multiValueLabel: (base) => ({
    ...base,
    padding: '2px 4px 2px 2px',
  }),
  multiValueRemove: (base) => ({
    ...base,
    borderRadius: 999,
    color: '#6e6e6e',
    cursor: 'pointer',
    ':hover': {
      backgroundColor: 'rgba(155, 0, 0, 0.08)',
      color: '#9b0000',
    },
  }),
  dropdownIndicator: () => ({ display: 'none' }),
  indicatorSeparator: () => ({ display: 'none' }),
  menu: (base) => ({
    ...base,
    marginTop: 8,
    borderRadius: 12,
    boxShadow: '0 8px 24px rgba(17, 17, 17, 0.08)',
    overflow: 'hidden',
    zIndex: 10060,
  }),
  menuPortal: (base) => ({ ...base, zIndex: 10060 }),
  menuList: (base) => ({
    ...base,
    padding: 4,
    maxHeight: 220,
  }),
  option: (base, state) => ({
    ...base,
    padding: '8px 10px',
    borderRadius: 8,
    backgroundColor:
      state.isFocused || state.isSelected ? '#f2f2f4' : 'transparent',
    color: '#0d0d0d',
    cursor: 'pointer',
    '&:active': { backgroundColor: '#e7e7ea' },
  }),
  noOptionsMessage: (base) => ({
    ...base,
    fontSize: 13,
    color: '#b7b7b7',
  }),
});

export const SelectedProject = ({ project }) => {
  const label =
    project?.title ||
    project?.name ||
    project?.projectName ||
    project?.label ||
    '—'

  return (
    <CustomInput
      label="Selected Project"
      value={label}
      readOnly
      className="project-session-modal__field"
    />
  )
}

export const SelectedCity = ({ city }) => (
  <CustomInput
    label="Selected City"
    value={city?.name || city?.shortName || '—'}
    readOnly
    className="project-session-modal__field"
  />
);

export const SelectedTask = ({ task }) => (
  <CustomInput
    label="Selected Task"
    value={task?.title || task?.name || '—'}
    readOnly
    className="project-session-modal__field"
  />
);

const getTaskTitle = task =>
  String(task?.title || task?.name || '').trim();

/** Hidden session title — task sessions only, prefilled with the task name. */
const HiddenTaskTitleField = ({ task, value }) => {
  if (!task) return null;
  return (
    <input
      type="hidden"
      name="title"
      value={value || getTaskTitle(task)}
      readOnly
    />
  );
};

const mapProjectToOption = project => {
  if (!project?.id) return null;
  return {
    value: String(project.id),
    label: project.title || project.name || 'Untitled project',
    raw: project,
  };
};

const toSessionProject = project => {
  if (!project?.id) return null;
  return {
    ...project,
    id: project.id,
    title: project.title || project.name || 'Untitled project',
    name: project.name || project.title || 'Untitled project',
    members: project.members || [],
  };
};

/**
 * Paginated project picker — same search + infinite-scroll pattern as
 * AddCollaboratorModal invite project field.
 */
const ProjectSelectField = ({
  project,
  onProjectChange,
  disabled = false,
  enabled = true,
  status = 'present',
}) => {
  const [inputValue, setInputValue] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [options, setOptions] = useState([]);
  const [nextPageUrl, setNextPageUrl] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  const latestRequest = useRef(0);
  const abortRef = useRef(null);

  const debouncedSetQuery = useMemo(
    () => debounce(query => setSearchQuery(query), SEARCH_DEBOUNCE_MS),
    []
  );

  const selectedOption = useMemo(() => mapProjectToOption(project), [project]);

  const mergedOptions = useMemo(() => {
    if (!selectedOption) return options;
    const exists = options.some(
      option => String(option.value) === String(selectedOption.value)
    );
    return exists ? options : [selectedOption, ...options];
  }, [options, selectedOption]);

  const loadProjects = useCallback(
    async ({ query = '', pageUrl = null, isLoadMore = false } = {}) => {
      const requestId = ++latestRequest.current;
      if (abortRef.current) abortRef.current.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      if (isLoadMore) {
        setIsLoadingMore(true);
      } else {
        setIsLoading(true);
        setIsLoadingMore(false);
      }

      const mapPage = response => {
        const result = response?.data?.result || {};
        const list = Array.isArray(result) ? result : result?.data || [];
        const mapped = list.map(mapProjectToOption).filter(Boolean);
        const next = Array.isArray(result)
          ? response?.data?.next_page_url
          : result?.next_page_url;
        return { mapped, next: next || null };
      };

      try {
        const response = await get(pageUrl || endpoints.getProjects, {
          signal: controller.signal,
          params: pageUrl
            ? undefined
            : {
                search: query.trim() || undefined,
                page: 1,
                page_size: PROJECT_PAGE_SIZE,
                ...(status ? { status } : {}),
                sort_by: 'name',
                sort_direction: 'asc',
              },
        });

        if (requestId !== latestRequest.current) return;

        const page = mapPage(response);
        setOptions(prev => {
          if (!isLoadMore) return page.mapped;
          const seen = new Set(prev.map(item => String(item.value)));
          return [
            ...prev,
            ...page.mapped.filter(item => !seen.has(String(item.value))),
          ];
        });
        setNextPageUrl(page.next);
      } catch {
        if (requestId !== latestRequest.current) return;
        if (!isLoadMore) {
          setOptions([]);
          setNextPageUrl(null);
        }
      } finally {
        if (requestId !== latestRequest.current) return;
        if (isLoadMore) {
          setIsLoadingMore(false);
        } else {
          setIsLoading(false);
        }
      }
    },
    [status]
  );

  useEffect(() => {
    if (!enabled || !menuOpen) return undefined;
    loadProjects({ query: searchQuery });
    return undefined;
  }, [enabled, menuOpen, searchQuery, loadProjects]);

  useEffect(() => {
    if (enabled) return undefined;
    setInputValue('');
    setSearchQuery('');
    setOptions([]);
    setNextPageUrl(null);
    setMenuOpen(false);
    return undefined;
  }, [enabled]);

  useEffect(
    () => () => {
      if (abortRef.current) abortRef.current.abort();
      debouncedSetQuery.cancel();
    },
    [debouncedSetQuery]
  );

  const handleInputChange = (newValue, meta) => {
    if (meta.action !== 'input-change') return;
    setInputValue(newValue);
    debouncedSetQuery(newValue);
  };

  const handleScrollToBottom = () => {
    if (isLoading || isLoadingMore || !nextPageUrl) return;
    loadProjects({
      query: searchQuery,
      pageUrl: nextPageUrl,
      isLoadMore: true,
    });
  };

  return (
    <div className="project-session-modal__field">
      <label className="project-session-modal__label" htmlFor="session-project">
        Project
      </label>
      <div className="project-collab-modal__search">
        <Select
          inputId="session-project"
          className="project-collab-modal__user-select"
          classNamePrefix="project-collab-modal-user"
          options={mergedOptions}
          value={selectedOption}
          onChange={option => {
            onProjectChange?.(toSessionProject(option?.raw || null));
            setInputValue('');
          }}
          inputValue={inputValue}
          onInputChange={handleInputChange}
          isSearchable
          isClearable={false}
          isDisabled={disabled}
          isLoading={isLoading}
          menuIsOpen={menuOpen}
          onMenuOpen={() => setMenuOpen(true)}
          onMenuClose={() => setMenuOpen(false)}
          onMenuScrollToBottom={handleScrollToBottom}
          placeholder="Search projects..."
          noOptionsMessage={() =>
            searchQuery ? 'No projects found' : 'Type to search projects'
          }
          loadingMessage={() => 'Searching...'}
          components={{
            IndicatorSeparator: null,
            DropdownIndicator: null,
          }}
          styles={collabSelectStyles()}
          menuPortalTarget={
            typeof document !== 'undefined' ? document.body : null
          }
          menuPosition="fixed"
          aria-label="Select Project"
        />
      </div>
    </div>
  );
};

const SelectedSessionTarget = ({
  task,
  project,
  onProjectChange,
  disabled = false,
  enableProjectSelect = false,
  isOpen = true,
}) => {
  if (task) return <SelectedTask task={task} />;
  if (enableProjectSelect || typeof onProjectChange === 'function') {
    return (
      <ProjectSelectField
        project={project}
        onProjectChange={onProjectChange}
        disabled={disabled}
        enabled={isOpen}
      />
    );
  }
  return <SelectedProject project={project} />;
};

export const getWorkingNoteError = (value, { required = true } = {}) => {
  const text = String(value || '');
  const trimmed = text.trim();
  if (required && !trimmed) return 'Working note is required';
  if (text.length > WORKING_NOTE_MAX_LENGTH) {
    return `Working note must be ${WORKING_NOTE_MAX_LENGTH} characters or less`;
  }
  return undefined;
};

export const NoteField = ({
  label,
  value,
  onChange,
  onBlur,
  name,
  maxLength = WORKING_NOTE_MAX_LENGTH,
  placeholder = 'Describe what you did...',
  helperText,
  error,
  disabled = false,
  showCount = true,
}) => {
  const length = String(value || '').length;
  const countHelper =
    showCount && maxLength != null ? `${length}/${maxLength}` : undefined;

  return (
    <CustomInput
      label={label}
      helperText={error ? undefined : helperText || countHelper}
      textarea
      name={name}
      value={value}
      onChange={onChange}
      onBlur={onBlur}
      placeholder={placeholder}
      {...(maxLength != null ? { maxLength } : {})}
      rows={3}
      disabled={disabled}
      error={error}
      className="project-session-modal__field"
    />
  );
};

export const ModalActions = ({
  onCancel,
  onConfirm,
  cancelLabel = 'Cancel',
  confirmLabel = 'Confirm',
  confirmDisabled = false,
  isSubmitting = false,
}) => (
  <div className="project-session-modal__actions">
    <CustomButton
      variant="danger"
      size="md"
      type="button"
      onClick={onCancel}
      disabled={isSubmitting}
    >
      {cancelLabel}
    </CustomButton>
    <CustomButton
      variant="primary"
      size="md"
      type="button"
      onClick={onConfirm}
      disabled={confirmDisabled || isSubmitting}
      loading={isSubmitting}
    >
      {confirmLabel}
    </CustomButton>
  </div>
);

const SessionModalShell = ({ isOpen, onClose, canClose = true, children }) => (
  <Modal
    isOpen={isOpen}
    toggle={canClose ? onClose : undefined}
    centered
    zIndex={10060}
    className="amok-modal amok-modal--session amok-modal--shadow-session"
    contentClassName="amok-modal__content"
    backdropClassName="amok-modal-backdrop--blur"
  >
    <ModalBody className="amok-modal__body project-session-modal__body">{children}</ModalBody>
  </Modal>
);

/**
 * Choose Solo vs Shadow before starting a task session.
 */
export const ChooseSessionTypeModal = ({
  isOpen,
  onClose,
  onSelectSolo,
  onSelectShadow,
}) => (
  <SessionModalShell isOpen={isOpen} onClose={onClose}>
    <div className="project-session-modal__main">
      <h3 className="project-session-modal__title">Start Session</h3>
      <p className="project-session-modal__subtitle">
        Choose a Solo or Shadow session for this task.
      </p>
    </div>
    <div className="project-session-modal__actions">
      <CustomButton
        variant="ghost"
        size="md"
        type="button"
        trailingIconSrc={ICONS.plus}
        trailingIconAlt=""
        onClick={onSelectShadow}
      >
        Shadow Session
      </CustomButton>
      <CustomButton
        variant="secondary"
        size="md"
        type="button"
        trailingIconSrc={ICONS.user}
        trailingIconAlt=""
        onClick={onSelectSolo}
      >
        Solo Session
      </CustomButton>
    </div>
  </SessionModalShell>
);

/**
 * StartSoloSessionModal — Figma 40004585:10224
 */
export const StartSoloSessionModal = ({
  isOpen,
  onClose,
  project,
  task = null,
  projectOptions = null,
  onProjectChange,
  onStart,
  isSubmitting = false,
}) => {
  const handleSubmit = (values) => {
    if (isSubmitting) return;
    const taskTitle = getTaskTitle(task);
    onStart?.({
      note: values.note.trim(),
      ...(taskTitle
        ? { title: String(values.title || taskTitle).trim() }
        : {}),
    });
  };

  const soloInitialValues = {
    ...SOLO_INITIAL_VALUES,
    ...(task ? { title: getTaskTitle(task) } : {}),
  };

  return (
    <SessionModalShell isOpen={isOpen} onClose={onClose} canClose={!isSubmitting}>
      <Formik
        key={isOpen ? 'solo-session-form' : 'solo-session-form-closed'}
        initialValues={soloInitialValues}
        validationSchema={soloSessionSchema}
        enableReinitialize
        validateOnBlur
        validateOnChange
        onSubmit={handleSubmit}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          handleSubmit: submitForm,
          setFieldTouched,
          resetForm,
        }) => (
          <Form
            className="project-session-modal__form"
            onSubmit={(e) => {
              e.preventDefault();
              submitForm();
            }}
          >
            <div className="project-session-modal__main">
              <h3 className="project-session-modal__title">Start Solo Session</h3>

              <div className="project-session-modal__fields">
                <SelectedSessionTarget
                  task={task}
                  project={project}
                  onProjectChange={onProjectChange}
                  disabled={isSubmitting}
                  isOpen={isOpen}
                />
                <HiddenTaskTitleField task={task} value={values.title} />
                <NoteField
                  label="Working Note"
                  name="note"
                  value={values.note}
                  onChange={event => {
                    handleChange(event);
                    setFieldTouched('note', true, false);
                  }}
                  onBlur={handleBlur}
                  placeholder="Add your working note"
                  disabled={isSubmitting}
                  error={touched.note && errors.note ? errors.note : undefined}
                />
              </div>
            </div>

            <ModalActions
              onCancel={() => {
                if (isSubmitting) return;
                resetForm();
                onClose();
              }}
              onConfirm={submitForm}
              confirmLabel="Start Session"
              confirmDisabled={!task && !project?.id}
              isSubmitting={isSubmitting}
            />
          </Form>
        )}
      </Formik>
    </SessionModalShell>
  );
};

/**
 * StartShadowSessionModal — Figma 40004571:20774
 * `variant="project"` keeps the project + collaborators picker.
 * `variant="city"` supports Public (no user_ids) or Private (city collaborators).
 */
export const StartShadowSessionModal = ({
  isOpen,
  onClose,
  project,
  task = null,
  onProjectChange,
  onStart,
  isSubmitting = false,
  variant = 'project',
  cities = [],
  selectedCity = null,
}) => {
  const isCity = variant === 'city';
  const projectId = project?.id || null;

  const hubCityOptions = useMemo(
    () =>
      (cities || [])
        .filter(item => item?.id)
        .map(item => ({
          value: String(item.id),
          label: item.name || item.shortName || 'City',
        })),
    [cities]
  );
  const useApiCities = isCity && hubCityOptions.length === 0;

  const handleSubmit = (values) => {
    if (isSubmitting) return;

    const visibility = isCity
      ? String(values.visibility || 'global').toLowerCase() === 'private'
        ? 'private'
        : 'global'
      : 'private';

    const selectedCollaborators = isCity
      ? visibility === 'private'
        ? values.collaborators || []
        : []
      : values.collaborators || [];

    // API requires user UUIDs — never emails.
    const userIds = Array.from(
      new Set(
        selectedCollaborators
          .map(option => getCollaboratorUserId(option) || String(option?.value || '').trim())
          .filter(id => isUuid(id))
      )
    );

    if (
      (isCity && visibility === 'private' && userIds.length === 0) ||
      (!isCity && userIds.length === 0)
    ) {
      return;
    }

    onStart?.({
      title: task
        ? String(values.title || getTaskTitle(task) || values.note || '').trim()
        : String(values.title || values.note || '').trim(),
      note: values.note.trim(),
      cityId: values.cityId || selectedCity?.id || null,
      visibility,
      userIds: isCity && visibility === 'global' ? [] : userIds,
    });
  };

  return (
    <SessionModalShell isOpen={isOpen} onClose={onClose} canClose={!isSubmitting}>
      <Formik
        key={
          isOpen
            ? `shadow-session-form-${variant}-${projectId || selectedCity?.id || 'none'}`
            : 'shadow-session-form-closed'
        }
        initialValues={
          isCity
            ? {
                ...CITY_SHADOW_INITIAL_VALUES,
                cityId: selectedCity?.id ? String(selectedCity.id) : '',
              }
            : {
                ...SHADOW_INITIAL_VALUES,
                ...(task ? { title: getTaskTitle(task) } : {}),
              }
        }
        validationSchema={isCity ? cityShadowSessionSchema : shadowSessionSchema}
        enableReinitialize
        validateOnBlur
        validateOnChange
        onSubmit={handleSubmit}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          handleSubmit: submitForm,
          setFieldValue,
          setFieldTouched,
          resetForm,
        }) => (
          <Form
            className="project-session-modal__form"
            onSubmit={(e) => {
              e.preventDefault();
              submitForm();
            }}
          >
            <div className="project-session-modal__main">
              <h3 className="project-session-modal__title">Start Shadow Session</h3>

                <div className="project-session-modal__fields">
                  {isCity ? (
                    <PreferredCitySelect
                      label="Select City"
                      placeholder="Select a city"
                      className="project-session-modal__field"
                      loadFromApi={useApiCities}
                      cityOptions={hubCityOptions}
                      value={values.cityId}
                      onChange={next => {
                        setFieldValue('cityId', next || '');
                        setFieldValue('collaborators', []);
                      }}
                      onBlur={() => handleBlur({ target: { name: 'cityId' } })}
                      error={
                        touched.cityId && errors.cityId ? errors.cityId : undefined
                      }
                    />
                  ) : (
                    <>
                      <SelectedSessionTarget
                        task={task}
                        project={project}
                        onProjectChange={nextProject => {
                          setFieldValue('collaborators', []);
                          onProjectChange?.(nextProject);
                        }}
                        disabled={isSubmitting}
                        isOpen={isOpen}
                      />
                      <HiddenTaskTitleField task={task} value={values.title} />
                    </>
                  )}

                  {isCity ? (
                    <div className="project-session-modal__field">
                      <span className="project-session-modal__label" id="city-shadow-visibility-label">
                        Visibility
                      </span>
                      <div
                        className="project-session-modal__visibility"
                        role="group"
                        aria-labelledby="city-shadow-visibility-label"
                      >
                        {CITY_VISIBILITY_OPTIONS.map(option => {
                          const isActive = values.visibility === option.value;
                          return (
                            <button
                              key={option.value}
                              type="button"
                              className={`project-session-modal__visibility-btn${
                                isActive
                                  ? ' project-session-modal__visibility-btn--active'
                                  : ''
                              }`}
                              aria-pressed={isActive}
                              disabled={isSubmitting}
                              onClick={() => {
                                setFieldValue('visibility', option.value);
                                if (option.value === 'global') {
                                  setFieldValue('collaborators', []);
                                }
                              }}
                            >
                              {option.label}
                            </button>
                          );
                        })}
                      </div>
                      <p className="project-session-modal__hint-inline">
                        {values.visibility === 'private'
                          ? 'Invite specific collaborators from this city.'
                          : 'Open to everyone in this city — no invite list needed.'}
                      </p>
                    </div>
                  ) : null}

                  {!isCity ? (
                    <ProjectInvitationCollaboratorSelect
                      projectId={projectId}
                      value={values.collaborators}
                      onChange={selected => {
                        setFieldValue('collaborators', selected || []);
                        setFieldTouched('collaborators', true, false);
                      }}
                      onBlur={() => setFieldTouched('collaborators', true)}
                      disabled={isSubmitting}
                      enabled={isOpen}
                      error={
                        touched.collaborators && errors.collaborators
                          ? errors.collaborators
                          : null
                      }
                    />
                  ) : null}

                  {isCity && values.visibility === 'private' ? (
                    <div className="project-collab-modal project-collab-modal--invite project-session-modal__member-search">
                      <CollaboratorSearchSelect
                        cityId={values.cityId || null}
                        value={values.collaborators}
                        onChange={selected => {
                          setFieldValue('collaborators', selected || []);
                          setFieldTouched('collaborators', true, false);
                        }}
                        onBlur={() => setFieldTouched('collaborators', true)}
                        disabled={isSubmitting || !values.cityId}
                        disabledReason={
                          !values.cityId ? 'Select a city first' : null
                        }
                        error={
                          touched.collaborators && errors.collaborators
                            ? typeof errors.collaborators === 'string'
                              ? errors.collaborators
                              : 'Select at least one collaborator from this city'
                            : null
                        }
                        enabled={isOpen}
                        inputId="city-shadow-session-collab-search"
                        allowCreate={false}
                        label="Search Collaborator"
                        placeholder="Search city members..."
                        menuZIndex={20040}
                      />
                    </div>
                  ) : null}

                <NoteField
                  label="Working Note"
                  name="note"
                  value={values.note}
                  onChange={event => {
                    handleChange(event);
                    setFieldTouched('note', true, false);
                  }}
                  onBlur={handleBlur}
                  placeholder="Add your working note"
                  disabled={isSubmitting}
                  error={touched.note && errors.note ? errors.note : undefined}
                />
              </div>
            </div>

            <ModalActions
              onCancel={() => {
                if (isSubmitting) return;
                resetForm();
                onClose();
              }}
              onConfirm={submitForm}
              confirmLabel="Start Shadow Session"
              confirmDisabled={!isCity && !task && !project?.id}
              isSubmitting={isSubmitting}
            />
          </Form>
        )}
      </Formik>
    </SessionModalShell>
  );
};

/**
 * JoinShadowSessionModal — Figma 40004585:8906
 */
export const JoinShadowSessionModal = ({
  isOpen,
  onClose,
  project,
  city = null,
  variant = 'project',
  collaborators = [],
  onJoin,
  isSubmitting = false,
}) => {
  const [note, setNote] = useState('');
  const [noteTouched, setNoteTouched] = useState(false);
  const isCity = variant === 'city' || Boolean(city?.id || city?.name);
  const noteError = noteTouched ? getWorkingNoteError(note) : undefined;

  useEffect(() => {
    if (!isOpen) {
      setNote('');
      setNoteTouched(false);
    }
  }, [isOpen]);

  const handleNoteChange = event => {
    const next = String(event.target.value || '').slice(
      0,
      WORKING_NOTE_MAX_LENGTH
    );
    setNote(next);
    if (noteTouched) setNoteTouched(true);
  };

  const handleJoin = () => {
    if (isSubmitting) return;
    setNoteTouched(true);
    if (getWorkingNoteError(note)) return;
    onJoin?.({ note: note.trim() });
  };

  return (
    <SessionModalShell isOpen={isOpen} onClose={onClose} canClose={!isSubmitting}>
      <div className="project-session-modal__main">
        <h3 className="project-session-modal__title">Join Shadow Session</h3>

        <div className="project-session-modal__fields">
          {isCity ? (
            <SelectedCity city={city} />
          ) : (
            <SelectedProject project={project} />
          )}

          <div className="project-session-modal__field">
            <div className="project-session-modal__note-label">
              <span className="project-session-modal__label">Collaborators in Session</span>
            </div>
            <div className="project-session-modal__collab-status-list" role="list">
              {collaborators.length === 0 ? (
                <p className="project-session-modal__hint-inline">
                  No collaborators in this session yet.
                </p>
              ) : (
                collaborators.map((person) => (
                  <div
                    key={person.id}
                    className="project-session-modal__collab-status-row"
                    role="listitem"
                  >
                    <div className="project-session-modal__collab-status-info">
                      <Avatar
                        src={person.avatar || person.src}
                        name={person.name}
                        size="sm"
                        className="project-session-modal__collab-avatar"
                      />
                      <span className="project-session-modal__collab-name">{person.name}</span>
                      {person.role ? (
                        <span className="project-session-modal__collab-role">{person.role}</span>
                      ) : null}
                    </div>
                    {person.status ? (
                      <span
                        className={`project-session-modal__status project-session-modal__status--${
                          SESSION_STATUS_CLASS[person.status] || 'invited'
                        }`}
                      >
                        {person.status}
                      </span>
                    ) : null}
                  </div>
                ))
              )}
            </div>
          </div>

          <NoteField
            label="Working Note"
            value={note}
            onChange={handleNoteChange}
            onBlur={() => setNoteTouched(true)}
            placeholder="Add your working note"
            disabled={isSubmitting}
            error={noteError}
          />
        </div>
      </div>

      <ModalActions
        onCancel={onClose}
        onConfirm={handleJoin}
        confirmLabel="Join Session"
        confirmDisabled={Boolean(getWorkingNoteError(note))}
        isSubmitting={isSubmitting}
      />
    </SessionModalShell>
  );
};

/**
 * Shown before logout when the user still has a live session.
 * Confirms that logout pauses the timer and does not exit the session.
 */
export const ActiveSessionLogoutModal = ({
  isOpen,
  onCancel,
  onConfirm,
  isSubmitting = false,
}) => (
  <SessionModalShell
    isOpen={isOpen}
    onClose={isSubmitting ? undefined : onCancel}
    canClose={!isSubmitting}
  >
    <div className="project-session-modal__main">
      <div className="project-session-modal__header">
        <h3 className="project-session-modal__title">
          You have an active session
        </h3>
        <p className="project-session-modal__subtitle">
          Logging out will pause your timer. Your session will not be exited —
          you can resume it when you sign back in.
        </p>
      </div>
    </div>
    <ModalActions
      onCancel={onCancel}
      onConfirm={onConfirm}
      cancelLabel="Stay signed in"
      confirmLabel="Pause & log out"
      isSubmitting={isSubmitting}
    />
  </SessionModalShell>
);

